import React, { useCallback, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOffOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';

import { useClaimDisposition } from '../shared';

interface ClaimCaseActionsProps {
  claimId: string;
  /** Called after a successful dismiss/snooze so the parent can advance selection. */
  onDispositioned?: () => void;
}

/**
 * Overflow menu for dispositioning a claim from the Review workspace.
 *
 * The Review sub-tab renders `ClaimReviewWorkspace` instead of the claims
 * `DataTable`, so it never gets `ClaimRowActions`' kebab. This mirrors the
 * Dismiss / "Remind me tomorrow" items for the review-queue (claim-case) view.
 */
export const ClaimCaseActions: React.FC<ClaimCaseActionsProps> = ({ claimId, onDispositioned }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const { dismiss, remindTomorrow, isUpdating } = useClaimDisposition({
    source: 'claims_review',
    onDispositioned,
  });

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleRemindTomorrow = useCallback(() => {
    handleClose();
    void remindTomorrow(claimId);
  }, [handleClose, remindTomorrow, claimId]);

  const handleDismiss = useCallback(() => {
    handleClose();
    void dismiss(claimId);
  }, [handleClose, dismiss, claimId]);

  return (
    <>
      <IconButton size="small" onClick={handleOpen} sx={{ p: 0.5 }} aria-label="Claim actions">
        {isUpdating ? <CircularProgress size={16} /> : <MoreVertIcon sx={{ fontSize: 18 }} />}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
      >
        <MenuItem onClick={handleRemindTomorrow} disabled={isUpdating}>
          <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
          <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
            Remind me tomorrow
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDismiss} disabled={isUpdating}>
          <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
          <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
            Dismiss
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
